// Web Push for event reminders on this device (see doc/feature/reminders.md). The service worker
// (`public/sw.js`) is the one owner of reminder notifications; the tab never shows one itself.

const api = (path, params = {}) => window.App.api(path, params, { silent: true })

/** Whether this browser can take push notifications at all. */
export function pushSupported() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** The bytes of a base64url string (padding optional): the VAPID key for `applicationServerKey`. */
export function base64UrlToBytes(key) {
  const base64 = String(key || '').replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

/** Whether a subscription's `applicationServerKey` (an ArrayBuffer, or null) is the base64url `key`. */
export function sameKey(buffer, key) {
  if (!buffer || !key) return false
  const a = new Uint8Array(buffer)
  const b = base64UrlToBytes(key)
  return a.length === b.length && a.every((byte, i) => byte === b[i])
}

let listening = false

/** Registers the service worker and resolves with its active registration. */
export async function registerWorker() {
  const container = navigator.serviceWorker
  if (!listening) {
    listening = true
    // A click on a notification while the app is open: go to its page here.
    container.addEventListener('message', (event) => {
      if (event.data?.type !== 'open' || !event.data.url) return
      const target = new URL(event.data.url, window.location.origin)
      if (target.pathname === window.location.pathname) window.App.go(target.hash || '#/')
      else window.location.assign(target.href)
    })
  }
  await container.register('/sw.js')
  return container.ready
}

async function browserSubscription() {
  const registration = await navigator.serviceWorker.getRegistration('/')
  return registration ? registration.pushManager.getSubscription() : null
}

/** Whether this browser holds a push subscription with notifications allowed; no API call. */
export async function pushOn() {
  if (!pushSupported() || Notification.permission !== 'granted') return false
  return Boolean(await browserSubscription())
}

/** This device's push: `{ supported, permission, subscribed, serverSubscribed }`. */
export async function devicePush() {
  if (!pushSupported()) return { supported: false, permission: 'default', subscribed: false, serverSubscribed: false }
  const [subscription, state] = await Promise.all([browserSubscription(), api('push/state')])
  return { supported: true, permission: Notification.permission, subscribed: Boolean(subscription), serverSubscribed: state.subscribed }
}

function subscribe(registration, key) {
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(key) })
}

function store(subscription) {
  const { endpoint, keys } = subscription.toJSON()
  return api('push/subscribe', { endpoint, p256dh: keys?.p256dh, auth: keys?.auth })
}

/** Asks for permission and subscribes this device; returns the permission (`granted` when on). */
export async function enablePush() {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission
  const registration = await registerWorker()
  const { key } = await api('push/state')
  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
    await subscription.unsubscribe()
    subscription = null
  }
  await store(subscription || (await subscribe(registration, key)))
  return permission
}

/** Turns push off on this device: the browser's subscription and the server's. */
export async function disablePush() {
  const subscription = await browserSubscription()
  await subscription?.unsubscribe()
  await api('push/unsubscribe')
}

/**
 * On boot of a person's session (not under sudo): registers the worker, renews a subscription made
 * with an older server key, and hands the server a subscription it lost.
 */
export async function syncPush() {
  if (!pushSupported()) return
  try {
    const registration = await registerWorker()
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return
    const state = await api('push/state')
    if (!sameKey(subscription.options.applicationServerKey, state.key)) {
      await subscription.unsubscribe()
      await store(await subscribe(registration, state.key))
    } else if (!state.subscribed) {
      await store(subscription)
    }
  } catch {
    // Push is a nicety: a failure here leaves the device without reminders, nothing else.
  }
}

/** On sign-out: drops the browser's subscription, so the next person here does not get these reminders. */
export async function forgetPush() {
  if (!pushSupported()) return
  try {
    const subscription = await browserSubscription()
    await subscription?.unsubscribe()
  } catch {
    // Nothing to forget.
  }
}
